import { createContext, useContext, useState, useCallback, useEffect } from 'react';

const BACKEND_URL = (() => {
  const url = process.env.REACT_APP_BACKEND_URL || '';
  if (!url) return '';
  return url.startsWith('http') ? url : 'https://' + url;
})();
const STORAGE_KEY = 'acodia_admin_token';

const AdminAuthContext = createContext(null);

export function AdminAuthProvider({ children }) {
  const [admin, setAdmin] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem(STORAGE_KEY));
  const [loading, setLoading] = useState(true);

  const saveToken = useCallback((t) => {
    setToken(t);
    if (t) localStorage.setItem(STORAGE_KEY, t);
    else localStorage.removeItem(STORAGE_KEY);
  }, []);

  const adminFetch = useCallback(async (url, options = {}) => {
    const t = token || localStorage.getItem(STORAGE_KEY);
    if (!t) throw new Error('Not authenticated');
    const headers = { ...options.headers, Authorization: `Bearer ${t}` };
    return fetch(url, { ...options, headers });
  }, [token]);

  const fetchAdmin = useCallback(async () => {
    const t = token || localStorage.getItem(STORAGE_KEY);
    if (!t) { setAdmin(null); setLoading(false); return; }
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/auth/me`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      if (res.ok) {
        setAdmin(await res.json());
      } else {
        saveToken(null);
        setAdmin(null);
      }
    } catch {
      setAdmin(null);
    } finally {
      setLoading(false);
    }
  }, [token, saveToken]);

  useEffect(() => { fetchAdmin(); }, []);

  const loginStep1 = async (email, password) => {
    const res = await fetch(`${BACKEND_URL}/api/admin/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const err = new Error(data.detail || 'Login failed');
      err.status = res.status;
      throw err;
    }
    return data;
  };

  const loginStep2 = async (email, totpCode) => {
    const res = await fetch(`${BACKEND_URL}/api/admin/auth/verify-totp`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, totp_code: totpCode }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const err = new Error(data.detail || 'TOTP verification failed');
      err.status = res.status;
      throw err;
    }
    saveToken(data.access_token);
    setAdmin(data.user);
    return data;
  };

  const logout = async () => {
    const t = token || localStorage.getItem(STORAGE_KEY);
    if (t) {
      try {
        await fetch(`${BACKEND_URL}/api/admin/auth/logout`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${t}` },
        });
      } catch {}
    }
    saveToken(null);
    setAdmin(null);
  };

  return (
    <AdminAuthContext.Provider
      value={{ admin, token, loading, loginStep1, loginStep2, logout, adminFetch, fetchAdmin }}
    >
      {children}
    </AdminAuthContext.Provider>
  );
}

export const useAdminAuth = () => {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) throw new Error('useAdminAuth must be used within AdminAuthProvider');
  return ctx;
};
